import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { SelectOption } from '@anytag/shared-types';
import { getValuesArray, SelectOptionValue } from '@anytag/shared-atoms';
import { useDeepCompareEffect } from '@anytag/shared-hooks';
import { SelectValueType, SelectorField, SelectorFieldProps } from '../selector-field';
import {
  AsyncSelectorSearchFieldProps,
  SingleAsyncSelectorSearchFieldProps,
  MultipleAsyncSelectorSearchFieldProps
} from './types';

// Function overloads for proper type inference based on `multiple` prop
export function AsyncSelectorSearchField<T extends string, TOption extends SelectOption<string>>(
  props: MultipleAsyncSelectorSearchFieldProps<T, TOption>
): React.ReactElement;
export function AsyncSelectorSearchField<T extends string, TOption extends SelectOption<string>>(
  props: SingleAsyncSelectorSearchFieldProps<T, TOption>
): React.ReactElement;
export function AsyncSelectorSearchField<T extends string, TOption extends SelectOption<string>>({
  name,
  loading,
  multiple,
  onChange,
  onSearch,
  disabled,
  searchProps,
  ...restProps
}: AsyncSelectorSearchFieldProps<T, TOption>): React.ReactElement {
  const [options, setOptions] = useState<TOption[]>([]);
  const { watch, setValue } = useFormContext<{ [name: string]: SelectValueType<TOption> }>();

  const value = watch(name) as SelectValueType<TOption>;
  const selectedValues = getValuesArray({ value, options, multiple }) as string[]; // use string[] internally for comparisons
  const needToFetchInitialOptions = options.length === 0 && selectedValues.length > 0;

  const handleSearch = async (
    keyword?: string,
    currentSelectedValues: SelectOptionValue<SelectOption<string>>[] = [],
    currentOptions: TOption[] = [],
    initialLoad = false
  ) => {
    const fetchedOptions = await onSearch(keyword, initialLoad);
    // Deduplicate options, keep and put selected options on the top
    const selectedOptions = currentOptions.filter((opt) => currentSelectedValues.includes(opt.value));
    const optionsMap = new Map([...selectedOptions, ...fetchedOptions].map((opt) => [opt.value, opt]));
    const newOptions = Array.from(optionsMap.values());
    // All currentSelectedValues MUST be in newOptions — if not, something is wrong with the API
    const isIncludesAllSelectedValues = currentSelectedValues.length
      ? currentSelectedValues.every((v) => newOptions.some((opt) => opt.value === v))
      : true;

    // In case something wrong, the currentSelectedValues not matching with the newOptions, reset the value
    if (!isIncludesAllSelectedValues) {
      setValue<string>(name, (multiple ? [] : '') as SelectValueType<TOption>, {
        shouldDirty: true,
        shouldValidate: true
      });
    }

    // Update options state
    setOptions(newOptions);
  };

  useDeepCompareEffect(() => {
    // Initialize: Fetch options based on initial value
    if (needToFetchInitialOptions) {
      handleSearch(undefined, selectedValues, [], true);
    }
  }, [selectedValues, needToFetchInitialOptions]); // options on init always empty, no need to pass into deps

  const commonProps: Pick<
    SelectorFieldProps<T, TOption>,
    'name' | 'loading' | 'options' | 'disabled' | 'searchProps' | 'onSearch'
  > = {
    name,
    loading,
    options,
    disabled: disabled || loading,
    searchProps: { ...searchProps, alwaysShow: true },
    onSearch: async (keyword, selectedValues) => {
      if (keyword) {
        await handleSearch(keyword, selectedValues, options); // pass lastest options each call
      }
    }
  };

  return multiple ? (
    <SelectorField<T, TOption>
      {...(restProps as Omit<Partial<MultipleAsyncSelectorSearchFieldProps<T, TOption>>, 'onSearch'>)}
      {...commonProps}
      multiple={true}
      onChange={(name, value, options) => {
        if (onChange) {
          (onChange as MultipleAsyncSelectorSearchFieldProps<T, TOption>['onChange'])?.(name, value, options);
        } else {
          setValue<string>(name, value, { shouldDirty: true, shouldValidate: true });
        }

        // Clean up options, keep only the selected options
        setOptions(options.filter((opt) => (value as string[]).includes(opt.value)));
      }}
    />
  ) : (
    <SelectorField<T, TOption>
      {...(restProps as Omit<Partial<SingleAsyncSelectorSearchFieldProps<T, TOption>>, 'onSearch'>)}
      {...commonProps}
      multiple={false}
      onChange={(name, value, options) => {
        if (onChange) {
          (onChange as SingleAsyncSelectorSearchFieldProps<T, TOption>['onChange'])?.(name, value, options);
        } else {
          setValue<string>(name, value, { shouldDirty: true, shouldValidate: true });
        }

        // Clean up options, keep only the selected option
        setOptions(options.filter((opt) => value === opt.value));
      }}
    />
  );
}
